import { format } from 'date-fns';

import { getFriendlyDate } from '../utils/humanReadable';
import Person from './Person';
import {
  CREATE_STATES,
  DRAFT_STATES,
  READ_ONLY_STATES,
  SIGNING_IN_PROGRESS_STATES,
  STATES_THAT_SHOW_LAST_MESSAGE,
  STATES_THAT_SHOW_WHO_HAS_SIGNED,
} from './Lease/States';
import { LEASING_WEB_API_PREFIX } from '../utils/queries';
import { baseUrl } from '../utils/apiConstants';
import { LEASE_VIEW_TYPES, SUMMARY_VIEW_SUBTYPES, type TagTone } from './Lease';
import { tagColorStatusMap, tagTextStatusMap } from '../constants/stringConstants';
import { isColandlordPath } from '../constants/routeConstants';
import type {
  LeaseActionDto,
  LeaseExtensionDto,
  LeaseStatus,
  LeasingLandlordDto,
  LeasingTenantDto,
  SignedUserDto,
} from '@zg-rentals/rental-manager-env';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import type { Message } from './Types';

class LeaseExtension {
  actions: Array<LeaseActionDto>;
  createdTime: number;
  endDate: number;
  id: string;
  journeyId: string;
  journeyVersionId: string;
  landlords: Array<LeasingLandlordDto>;
  leaseId: string;
  listingAlias: string;
  messageData: Message;
  ratifiedDate: number;
  recentActivity: string;
  sharedDate: number;
  signedUsers: Array<SignedUserDto>;
  startDate: number;
  status: LeaseStatus;
  tenants: Array<LeasingTenantDto>;
  updatedTime: number;
  userActionExpected: boolean;

  constructor(args: LeaseExtensionDto) {
    this.actions = args?.actions;
    this.createdTime = args?.createdTime ? args.createdTime : null;
    this.endDate = args?.endDate ? args.endDate : null;
    this.id = String(args?.id ?? '');
    this.journeyId = args?.journeyId;
    this.journeyVersionId = args?.journeyVersionId;
    this.landlords = LeaseExtension.addPersonsFromApi(args?.landlords);
    this.leaseId = String(args?.leaseId ?? '');
    this.listingAlias = args?.listingAlias;
    this.messageData = args?.messageData;
    this.ratifiedDate = args?.ratifiedDate;
    this.recentActivity = args?.recentActivity;
    this.sharedDate = args?.sharedDate ? args.sharedDate : null;
    this.signedUsers = args?.signedUsers;
    this.startDate = args?.startDate ? args.startDate : null;
    this.status = args?.status;
    this.tenants = LeaseExtension.addPersonsFromApi(args?.tenants);
    this.updatedTime = args?.updatedTime ? args.updatedTime : null;
    this.userActionExpected = args?.userActionExpected;
  }

  static addPersonsFromApi = (personsFromAPI) => (personsFromAPI || []).map((params) => new Person(params));

  static sortByLastCreatedFirst = (leaseExtensionA: LeaseExtension, leaseExtensionB: LeaseExtension): number =>
    leaseExtensionB.createdTime - leaseExtensionA.createdTime;

  static sortByLastUpdatedFirst = (leaseExtensionA: LeaseExtension, leaseExtensionB: LeaseExtension): number =>
    (leaseExtensionB.updatedTime || leaseExtensionB.createdTime) -
    (leaseExtensionA.updatedTime || leaseExtensionA.createdTime);

  static getPDFLink(leaseExtensionId: string, isDraft = false, pathname: string = null) {
    if (pathname == null) {
      pathname = window.location.pathname;
    }

    const isColandlord = isColandlordPath(pathname);

    return isColandlord
      ? `${baseUrl}/${LEASING_WEB_API_PREFIX}/ouid/downloadFromOuid${window.location.search}&draft=${isDraft}`
      : `${baseUrl}/${LEASING_WEB_API_PREFIX}/documents/download?leaseArtifactId=${leaseExtensionId}&leaseArtifactType=${LeaseArtifactType.LEASE_EXTENSION_RENEWAL}&draft=${isDraft}`;
  }

  static getDashboardPath(pathname: string = null): string {
    let newPath = '';

    if (pathname != null) {
      newPath = pathname;
    } else if (typeof window !== 'undefined') {
      newPath = window.location.pathname;
    }

    const propertyRegex = /(.*\/property\/[A-Za-z_\-0-9]+)\/?(\?.*)?/;
    const landlordRegexGroups = newPath.match(propertyRegex);
    if (landlordRegexGroups != null) {
      return `${landlordRegexGroups[1]}`;
    }

    const tenantWelcomeRegexGroups = newPath.match(/(.*\/tenant\/welcome)\/?(\?.*)?/);
    if (tenantWelcomeRegexGroups != null) {
      return `/renter-hub/leasing`;
    }

    const tenantRegexGroups = newPath.match(/(.*\/renter-hub\/leasing)\/?(\?.*)?/);
    if (tenantRegexGroups != null) {
      return `${tenantRegexGroups[1]}`;
    }

    const colandlordRegexGroups = newPath.match(/(.*\/colandlord)\/?(\?.*)?/);
    if (colandlordRegexGroups != null) {
      return `${colandlordRegexGroups[1]}`;
    }

    // Test fallback
    const testPath = `/property/somePropertyId/`;
    // eslint-disable-next-line no-console
    console.error(`Could not generate a path, are we in a test environment? Using this fake path instead: ${testPath}`);
    return testPath;
  }

  static getRootPath(leaseId: string, leaseExtensionId: string, pathname: string = null): string {
    return `${LeaseExtension.getDashboardPath(pathname)}/lease/${leaseId}/renewal/${leaseExtensionId}`;
  }

  static getWizardPath = (leaseId: string, leaseExtensionId: string, pathname?: string) =>
    `${LeaseExtension.getRootPath(leaseId, leaseExtensionId, pathname)}`;

  static getSummaryPathBase(leaseId: string, leaseExtensionId: string, pathname?: string) {
    const rootPath = LeaseExtension.getRootPath(leaseId, leaseExtensionId, pathname);
    const isLandlord = rootPath.indexOf('property') >= 0;

    return isLandlord ? `${rootPath}/${LEASE_VIEW_TYPES.SUMMARY}` : rootPath;
  }

  static getSummaryPath(leaseId: string, leaseExtensionId: string, pathname?: string) {
    const summaryPath = LeaseExtension.getSummaryPathBase(leaseId, leaseExtensionId, pathname);
    const isColandlord = isColandlordPath(summaryPath);

    return isColandlord ? `${summaryPath}${window.location.search}` : summaryPath;
  }

  static getPreviewPath(leaseId: string, leaseExtensionId: string, pathname?: string) {
    const rootPath = LeaseExtension.getRootPath(leaseId, leaseExtensionId, pathname);
    const isColandlord = isColandlordPath(rootPath);
    const previewPath = `${rootPath}/${SUMMARY_VIEW_SUBTYPES.PREVIEW}`;

    return isColandlord ? `${previewPath}${window.location.search}` : previewPath;
  }

  isEditable = (): boolean => !this.isInSigningState() && !this.isReadOnly();

  isInSigningState = (): boolean => SIGNING_IN_PROGRESS_STATES.has(this.status);

  isDraft = (): boolean => DRAFT_STATES.has(this.status);

  isCreating = (): boolean => CREATE_STATES.has(this.status);

  isReadOnly = (): boolean => READ_ONLY_STATES.has(this.status);

  getFriendlyStartDate() {
    return this.startDate != null ? getFriendlyDate(new Date(this.startDate)) : null;
  }

  getFriendlyEndDate() {
    return this.endDate != null ? getFriendlyDate(new Date(this.endDate)) : null;
  }

  getFriendlyCreationDate() {
    return this.createdTime != null ? getFriendlyDate(new Date(this.createdTime)) : null;
  }

  getFriendlyUpdatedDate() {
    return this.updatedTime != null ? getFriendlyDate(new Date(this.updatedTime)) : null;
  }

  getFriendlySharedDate() {
    return this.sharedDate != null ? getFriendlyDate(new Date(this.sharedDate)) : null;
  }

  getPDFLink = (isDraft?: boolean): string => LeaseExtension.getPDFLink(this.id, isDraft);

  // Builds a relative path for this Lease given the current path
  getRootPath = (pathname: string = null): string => LeaseExtension.getRootPath(this.leaseId, this.id, pathname);

  getWizardPath = (pathname?: string): string => LeaseExtension.getWizardPath(this.leaseId, this.id, pathname);

  getPreviewPath = (pathname?: string): string => LeaseExtension.getPreviewPath(this.leaseId, this.id, pathname);

  hasEnded = (): boolean => this.endDate != null && new Date(this.endDate) < new Date();

  hasColandlord = (): boolean => this.landlords.length > 1;

  showWhoHasSigned = (): boolean => STATES_THAT_SHOW_WHO_HAS_SIGNED.has(this.status);

  showLastMessage = (): boolean => STATES_THAT_SHOW_LAST_MESSAGE.has(this.status);

  getHumanReadableStatus(): string {
    if (this.userActionExpected) {
      return 'Awaiting your signature';
    }

    return tagTextStatusMap[this.status] || this.status;
  }

  getBadgeColor(): TagTone {
    if (this.userActionExpected) {
      return 'yellow';
    }

    return (tagColorStatusMap[this.status] as TagTone) || 'blue';
  }

  getDownloadFileName() {
    const parts = ['Lease renewal'];

    if (this.tenants[0]?.firstName) {
      parts.push(this.tenants[0].firstName);
    }
    if (this.tenants[0]?.lastName) {
      parts.push(this.tenants[0].lastName);
    }
    parts.push(format(this.createdTime, 'yyyyMMdd'));
    return parts.join('-');
  }
}

export default LeaseExtension;
